import { Component } from '@theme/component';
import { ThemeEvents, ZoomMediaSelectedEvent } from '@theme/events';
import { StandardEvents, ProductSelectEvent } from '@shopify/events';

/**
 * A custom element that renders a media gallery.
 *
 * @typedef {object} Refs
 * @property {import('./zoom-dialog').ZoomDialog} [zoomDialogComponent] - The zoom dialog component.
 * @property {import('./slideshow').Slideshow} [slideshow] - The slideshow component.
 * @property {HTMLElement[]} [media] - The media elements.
 *
 * @extends Component<Refs>
 */
export class MediaGallery extends Component {
  connectedCallback() {
    super.connectedCallback();

    // A gallery can be detached and reconnected during Shopify section morphs.
    // AbortController signals cannot be reused once aborted, so create a fresh
    // controller before registering listeners again.
    if (this.#controller.signal.aborted) {
      this.#controller = new AbortController();
    }

    const { signal } = this.#controller;
    const target = this.closest('.shopify-section, dialog');

    target?.addEventListener(
      StandardEvents.productSelect,
      this.#handleProductSelect,
      { signal }
    );

    this.refs.zoomDialogComponent?.addEventListener(
      ThemeEvents.zoomMediaSelected,
      this.#handleZoomMediaSelected,
      {
        signal,
      }
    );
  }

  #controller = new AbortController();

  /**
   * Identifies the newest product-selection request. Variant requests can
   * finish out of order on slower connections; only the newest response may
   * replace the gallery.
   */
  #productSelectRequestId = 0;

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#controller.abort();
  }

  /**
   * Handles a product select event by replacing the current media gallery
   * with the freshly rendered gallery returned by Shopify.
   *
   * @param {ProductSelectEvent} event
   */
  #handleProductSelect = (event) => {
    if (
      !(event.target instanceof Element) ||
      event.target.closest('product-card')
    ) {
      return;
    }

    const requestId = ++this.#productSelectRequestId;

    event.promise
      .then(({ detail }) => {
        if (
          requestId !== this.#productSelectRequestId ||
          !this.isConnected ||
          !detail?.html
        ) {
          return;
        }

        const { html } = detail;
        const newMediaGallery =
          html.querySelector('media-gallery');

        if (!newMediaGallery) return;

        // Hero and Low Light are the first two product photos. Start both
        // downloads immediately so the second slide is ready when the shopper
        // swipes or taps the next arrow.
        const primaryImages = newMediaGallery.querySelectorAll(
          'slideshow-component img.product-media__image'
        );

        primaryImages.forEach((image, index) => {
          if (index > 1 || !(image instanceof HTMLImageElement)) return;
          image.loading = 'eager';
          image.fetchPriority = index === 0 ? 'high' : 'auto';
        });

        this.replaceWith(newMediaGallery);

        // Let the newly connected slideshow finish measuring its slides, then
        // reset it to the Hero image. This avoids carrying a stale scroll
        // position into the freshly rendered gallery on mobile Safari.
        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            if (!newMediaGallery.isConnected) return;
            newMediaGallery.slideshow?.select(0, undefined, {
              animate: false,
            });
          });
        });
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') {
          console.warn(
            '[media-gallery] Event promise rejected:',
            error
          );
        }
      });
  };

  /**
   * Handles the zoom-media:selected event.
   *
   * @param {ZoomMediaSelectedEvent} event
   */
  #handleZoomMediaSelected = async (event) => {
    this.slideshow?.select(
      event.detail.index,
      undefined,
      { animate: false }
    );
  };

  /**
   * Zooms the media gallery.
   *
   * @param {number} index
   * @param {PointerEvent} event
   */
  zoom(index, event) {
    this.refs.zoomDialogComponent?.open(index, event);
  }

  /**
   * Preloads an image.
   *
   * @param {number} index
   */
  preloadImage(index) {
    const zoomDialogMedia =
      this.refs.zoomDialogComponent?.refs.media[index];

    if (!zoomDialogMedia) return;

    this.refs.zoomDialogComponent?.loadHighResolutionImage(
      zoomDialogMedia
    );
  }

  get slideshow() {
    return this.refs.slideshow;
  }

  get media() {
    return this.refs.media;
  }

  get presentation() {
    return this.dataset.presentation;
  }
}

if (!customElements.get('media-gallery')) {
  customElements.define('media-gallery', MediaGallery);
}
